// Site time zones (SITE_TIME_ZONE_WORDS.md, decided 2026-10-26).
// Each site carries its own zone. Four for now; Robert adds more when a plant needs one.
// This file is the site's zone only: picking it, showing it, and reading a clock in it.
// Using it for every time the app shows and for the night jobs is the next step (PR B).

export interface SiteZone {
  /** IANA zone the clock reads in. Daylight saving rides with it. */
  id: string;
  /** What Robert reads: "Central". */
  name: string;
  /** Short tag for lists that mix sites. Blank for Eastern, which reads plain as it always has. */
  tag: string;
  /** Where it applies, for the picker. */
  where: string;
}

export const SITE_ZONES: readonly SiteZone[] = [
  { id: "America/New_York", name: "Eastern", tag: "", where: "KY, IN, GA, NC, OH, PA" },
  { id: "America/Chicago", name: "Central", tag: "CT", where: "MN, IA, IL, WI, TX, KS, NE, ND, AL" },
  { id: "America/Denver", name: "Mountain", tag: "MT", where: "MT, CO, western SD" },
  { id: "America/Los_Angeles", name: "Pacific", tag: "PT", where: "WA, OR, CA" },
];

/** Every site read Eastern before zones existed. A site with no zone and no clear state still does. */
export const DEFAULT_ZONE = "America/New_York";

export function isSiteZone(id: string): boolean {
  return SITE_ZONES.some((z) => z.id === id);
}

export function zoneInfo(id: string): SiteZone {
  return SITE_ZONES.find((z) => z.id === id) ?? SITE_ZONES[0]!;
}

/**
 * States that sit in one of the four zones. A state split between two zones (KS, ND, SD, NE, TX, IN, KY, TN, FL, MI,
 * OR, ID) is left out on purpose: the plant's own clock decides, so Add Site asks once.
 */
const ONE_ZONE: Record<string, string> = {
  // Eastern
  CT: "America/New_York", DE: "America/New_York", DC: "America/New_York", GA: "America/New_York", ME: "America/New_York",
  MD: "America/New_York", MA: "America/New_York", NH: "America/New_York", NJ: "America/New_York", NY: "America/New_York",
  NC: "America/New_York", OH: "America/New_York", PA: "America/New_York", RI: "America/New_York", SC: "America/New_York",
  VT: "America/New_York", VA: "America/New_York", WV: "America/New_York",
  // Central
  AL: "America/Chicago", AR: "America/Chicago", IL: "America/Chicago", IA: "America/Chicago", LA: "America/Chicago",
  MN: "America/Chicago", MS: "America/Chicago", MO: "America/Chicago", OK: "America/Chicago", WI: "America/Chicago",
  // Mountain
  CO: "America/Denver", MT: "America/Denver", NM: "America/Denver", UT: "America/Denver", WY: "America/Denver",
  // Pacific
  CA: "America/Los_Angeles", NV: "America/Los_Angeles", WA: "America/Los_Angeles",
};

/** The zone a state points to, or blank when the state has two zones (or is unknown) and the plant must say. */
export function zoneFromState(state: string): string {
  return ONE_ZONE[state.trim().toUpperCase()] ?? "";
}

/**
 * The site's zone: the one saved on the site, else the one its state points to, else Eastern.
 * The five migrated sites have none saved and read Eastern; Mankato (MN) reads Central until Robert confirms it.
 */
export function siteZone(site: { timeZone?: string | null; state?: string | null } | null | undefined): string {
  const saved = site?.timeZone ?? "";
  if (isSiteZone(saved)) return saved;
  const fromState = zoneFromState(site?.state ?? "");
  return fromState || DEFAULT_ZONE;
}

/** "3:58 PM": the clock at the plant right now. */
export function clockIn(zone: string, at: Date): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" }).format(at);
}
